// Pair-endpoint snippet for Unity / C#. Mirrors the Godot reference
// implementation: persists the player token + a stable install id, exposes
// a singleton-style API, and emits C# events on success / failure.

export function buildPairUnitySnippet(apiKey: string, functionsBaseUrl: string): string {
  return `// Assets/PlayerBase/PlayerBasePairing.cs
// One-off pairing handshake. Add this to a bootstrap GameObject in
// your first scene (it survives scene loads via DontDestroyOnLoad), or
// instantiate it manually before your pairing UI shows.
//
// Flow:
//   1. Player taps the recruitment button in Discord -> bot DMs them a
//      short code.
//   2. Your in-game pairing UI calls PlayerBasePairing.Instance.Redeem(code).
//   3. On success, OnPaired fires with the playerToken. The token plus
//      the install id are written to PlayerPrefs and reused on every
//      subsequent /events call (see PlayerBaseEvents).
//
// The install id is generated once per device and kept across re-pairs
// (Unpair() leaves it intact) so the backend can correlate sessions
// from the same install.

using System;
using System.Collections;
using System.Security.Cryptography;
using System.Text;
using UnityEngine;
using UnityEngine.Networking;

public class PlayerBasePairing : MonoBehaviour
{
    public static PlayerBasePairing Instance { get; private set; }

    public event Action<string> OnPaired;
    public event Action<string> OnPairFailed;

    public bool IsPaired => !string.IsNullOrEmpty(_playerToken);
    public string PlayerToken => _playerToken;
    public string InstallId => _installId;

    private const string ApiKey = "${apiKey}";
    private const string PairUrl = "${functionsBaseUrl}/pair";
    private const string PrefPlayerToken = "playerbase.player_token";
    private const string PrefInstallId = "playerbase.install_id";

    private string _playerToken = "";
    private string _installId = "";

    private void Awake()
    {
        if (Instance != null && Instance != this) { Destroy(gameObject); return; }
        Instance = this;
        DontDestroyOnLoad(gameObject);

        _playerToken = PlayerPrefs.GetString(PrefPlayerToken, "");
        _installId = PlayerPrefs.GetString(PrefInstallId, "");
        if (string.IsNullOrEmpty(_installId))
        {
            _installId = Guid.NewGuid().ToString();
            PlayerPrefs.SetString(PrefInstallId, _installId);
            PlayerPrefs.Save();
        }
    }

    // Call from your in-game pairing UI once the player has pasted the
    // code from the Discord DM. Listen to OnPaired / OnPairFailed to
    // advance the UI.
    public void Redeem(string pairingCode)
    {
        StartCoroutine(RedeemCoroutine(pairingCode));
    }

    // Wipe the saved token. Use for a "Sign out / unpair" affordance in
    // settings. Install id is kept so a later re-pair correlates with
    // the same install.
    public void Unpair()
    {
        _playerToken = "";
        PlayerPrefs.DeleteKey(PrefPlayerToken);
        PlayerPrefs.Save();
    }

    private IEnumerator RedeemCoroutine(string pairingCode)
    {
        string code = (pairingCode ?? "").Trim();
        string body = "{\\"code\\":\\"" + JsonEscape(code)
            + "\\",\\"installId\\":\\"" + JsonEscape(_installId) + "\\"}";
        string signature = HmacSha256Hex(ApiKey, body);

        using var req = new UnityWebRequest(PairUrl, "POST");
        req.uploadHandler = new UploadHandlerRaw(Encoding.UTF8.GetBytes(body));
        req.downloadHandler = new DownloadHandlerBuffer();
        req.SetRequestHeader("content-type", "application/json");
        req.SetRequestHeader("x-playerbase-signature", signature);

        yield return req.SendWebRequest();

        if (req.result != UnityWebRequest.Result.Success || req.responseCode != 200)
        {
            string reason = "http_" + req.responseCode;
            try
            {
                var err = JsonUtility.FromJson<ErrorBody>(req.downloadHandler.text);
                if (err != null && !string.IsNullOrEmpty(err.error)) reason = err.error;
            }
            catch { /* leave reason as http_<code> */ }
            OnPairFailed?.Invoke(reason);
            yield break;
        }

        PairResponse resp;
        try { resp = JsonUtility.FromJson<PairResponse>(req.downloadHandler.text); }
        catch { OnPairFailed?.Invoke("bad_response"); yield break; }
        if (resp == null || string.IsNullOrEmpty(resp.playerToken))
        {
            OnPairFailed?.Invoke("bad_response");
            yield break;
        }

        _playerToken = resp.playerToken;
        PlayerPrefs.SetString(PrefPlayerToken, _playerToken);
        PlayerPrefs.Save();
        OnPaired?.Invoke(_playerToken);
    }

    [Serializable] private class PairResponse { public string playerToken; }
    [Serializable] private class ErrorBody { public string error; }

    internal static string JsonEscape(string s)
    {
        var sb = new StringBuilder(s.Length + 2);
        foreach (char c in s)
        {
            switch (c)
            {
                case '"': sb.Append("\\\\\\""); break;
                case '\\\\': sb.Append("\\\\\\\\"); break;
                case '\\n': sb.Append("\\\\n"); break;
                case '\\r': sb.Append("\\\\r"); break;
                case '\\t': sb.Append("\\\\t"); break;
                default:
                    if (c < 0x20) sb.AppendFormat("\\\\u{0:x4}", (int)c);
                    else sb.Append(c);
                    break;
            }
        }
        return sb.ToString();
    }

    internal static string HmacSha256Hex(string key, string body)
    {
        using var h = new HMACSHA256(Encoding.UTF8.GetBytes(key));
        byte[] hash = h.ComputeHash(Encoding.UTF8.GetBytes(body));
        var sb = new StringBuilder(hash.Length * 2);
        foreach (byte b in hash) sb.Append(b.ToString("x2"));
        return sb.ToString();
    }
}
`;
}
